import * as Sentry from "@sentry/react";

// Chrome's built-in Google Translate replaces the page's text nodes with <font> elements. React
// still holds the original nodes, so when it later removes one, or inserts next to one, the DOM
// call throws NotFoundError in the middle of a commit, and React 17 unmounts the whole app: a
// blank page (REACT-WEBAPP-81, https://github.com/facebook/react/issues/11538).
//
// These patches make the two calls React relies on tolerate a node that is no longer where React
// left it. Translated text that React later updates in place still won't change on screen, since
// React updates the detached original node; that's stale text rather than a crash.
let isInstalled = false;

export const installTranslateDomPatch = () => {
  if (isInstalled || typeof Node !== "function" || !Node.prototype) {
    return;
  }
  isInstalled = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T
  ): T {
    if (child.parentNode !== this) {
      logMismatch("removeChild");
      // Translate moved it rather than replacing it: remove it from where it is now, so it
      // doesn't linger on screen.
      if (child.parentNode) {
        originalRemoveChild.call(child.parentNode, child);
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      logMismatch("insertBefore");
      // The right parent, possibly the wrong position: better than no node at all.
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};

const logMismatch = (method: string) => {
  Sentry.addBreadcrumb({
    category: "dom",
    level: Sentry.Severity.Warning,
    message: `translate-dom-patch: ${method} parent mismatch`,
  });
};
